import { type CSSProperties, useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useActiveCampaign } from '../context/CampaignContext';
import { getCampaignTheme } from '@shared/campaigns';

/** Hand-authored lotus rangoli mandala (line art). */
function Mandala({ className, color, style }: { className?: string; color: string; style?: CSSProperties }) {
  const ring = Array.from({ length: 12 });
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} fill="none" stroke={color} strokeWidth="0.5" strokeLinecap="round" aria-hidden="true">
      <circle cx="50" cy="50" r="3.5" />
      <circle cx="50" cy="50" r="1.2" fill={color} stroke="none" />
      {ring.map((_, i) => (
        <path key={`i${i}`} transform={`rotate(${i * 30} 50 50)`} d="M50 46 C 45 36 45 26 50 20 C 55 26 55 36 50 46 Z" />
      ))}
      <circle cx="50" cy="50" r="24" strokeDasharray="0.5 2.5" />
      {ring.map((_, i) => (
        <path key={`o${i}`} transform={`rotate(${i * 30 + 15} 50 50)`} d="M50 24 C 46 18 46 10 50 4 C 54 10 54 18 50 24 Z" />
      ))}
      <circle cx="50" cy="50" r="34" strokeDasharray="0.5 3.5" />
      {ring.map((_, i) => (
        <circle key={`d${i}`} transform={`rotate(${i * 30} 50 50)`} cx="50" cy="11" r="0.9" fill={color} stroke="none" />
      ))}
    </svg>
  );
}

/** Gold diamond divider motif (◆ ◆ ◆ with flanking rules). */
function DiamondDivider({ className, color, style }: { className?: string; color: string; style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 120 10" className={className} style={style} fill={color} stroke={color} strokeWidth="0.7" aria-hidden="true">
      <line x1="4" y1="5" x2="47" y2="5" />
      <path d="M51 5 L55 1.5 L59 5 L55 8.5 Z" />
      <path d="M60 5 L65 0.5 L70 5 L65 9.5 Z" />
      <path d="M71 5 L75 1.5 L79 5 L75 8.5 Z" />
      <line x1="83" y1="5" x2="116" y2="5" />
    </svg>
  );
}

/** Hand-drawn clay diya (oil lamp) with a flickering flame. Gradients are
 *  referenced from the shared <defs> rendered once by CampaignHeroDecor. */
function Diya({ delay = 0, reduce = false }: { delay?: number; reduce?: boolean }) {
  return (
    <div className="relative" style={{ width: 46, height: 42 }} aria-hidden>
      {/* warm glow pool around the flame */}
      <motion.span
        className="absolute left-1/2 -translate-x-1/2 rounded-full"
        style={{ bottom: 10, width: 34, height: 34, background: 'radial-gradient(circle, rgba(251,146,60,0.75), rgba(251,146,60,0) 68%)', filter: 'blur(3px)' }}
        animate={reduce ? undefined : { opacity: [0.55, 1, 0.6], scale: [0.9, 1.14, 0.95] }}
        transition={reduce ? undefined : { duration: 1.5, delay, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* flame */}
      <motion.svg
        width="12" height="18" viewBox="0 0 12 18" fill="none"
        className="absolute left-1/2 -translate-x-1/2"
        style={{ bottom: 15, transformOrigin: 'bottom center' }}
        animate={reduce ? undefined : { scaleY: [1, 1.2, 0.94, 1.12, 1], scaleX: [1, 0.93, 1.04, 0.97, 1], rotate: [0, 2.5, -2, 1.5, 0] }}
        transition={reduce ? undefined : { duration: 0.7, delay, repeat: Infinity, ease: 'easeInOut' }}
      >
        <path d="M6 18 C 1 12.5, 1.2 6.5, 6 0 C 10.8 6.5, 11 12.5, 6 18 Z" fill="url(#zs-flame)" />
        <path d="M6 18 C 3.6 14, 3.6 9, 6 4.5 C 8.4 9, 8.4 14, 6 18 Z" fill="#fff7cc" opacity="0.85" />
      </motion.svg>
      {/* clay bowl */}
      <svg width="46" height="16" viewBox="0 0 46 16" fill="none" className="absolute bottom-0 left-0">
        <path d="M1 3 Q 23 18 45 3 Q 23 8 1 3 Z" fill="url(#zs-clay)" />
        <path d="M1 3 Q 23 8 45 3" stroke="#fdba74" strokeWidth="1.1" fill="none" opacity="0.65" />
      </svg>
    </div>
  );
}

/** Glowing floating sky lantern (akash kandil). */
function SkyLantern({ scale = 1 }: { scale?: number }) {
  return (
    <svg width={30 * scale} height={44 * scale} viewBox="0 0 30 44" fill="none" aria-hidden>
      <ellipse cx="15" cy="22" rx="15" ry="18" fill="url(#zs-lantern-glow)" />
      <path d="M11 7 Q 15 3 19 7" stroke="#fcd34d" strokeWidth="1.2" fill="none" />
      <path d="M7 11 Q 7 8 15 8 Q 23 8 23 11 L 21 32 Q 21 36 15 36 Q 9 36 9 32 Z" fill="url(#zs-lantern)" stroke="#fb923c" strokeWidth="0.7" />
      <path d="M15 8 L 15 36" stroke="#fb923c" strokeWidth="0.5" opacity="0.45" />
      <path d="M11 9 L 11.5 35" stroke="#fb923c" strokeWidth="0.4" opacity="0.3" />
      <path d="M19 9 L 18.5 35" stroke="#fb923c" strokeWidth="0.4" opacity="0.3" />
      <ellipse cx="15" cy="32" rx="3.5" ry="2.6" fill="#fff7cc" opacity="0.95" />
      <path d="M15 36 L 15 42" stroke="#b45309" strokeWidth="1" />
      <circle cx="15" cy="43" r="1.1" fill="#fcd34d" />
    </svg>
  );
}

const FW_RADIUS = 104;
// A burst = 3 concentric rings of comet streaks (tails trailing toward centre).
const BURST_RINGS = [
  { rf: 1, count: 14 },
  { rf: 0.66, count: 10 },
  { rf: 0.4, count: 7 },
];

/** A 3-ring firework burst of tailed comet streaks. */
function Burst({ colorA, colorB, delay }: { colorA: string; colorB: string; delay: number }) {
  return (
    <>
      {BURST_RINGS.map((ring, ri) =>
        Array.from({ length: ring.count }).map((_, i) => {
          const angle = (i / ring.count) * Math.PI * 2 + ri * 0.4;
          const R = FW_RADIUS * ring.rf;
          const dx = Math.cos(angle) * R;
          const dy = Math.sin(angle) * R;
          const deg = (angle * 180) / Math.PI + 90; // align streak length to travel direction
          const color = (i + ri) % 2 === 0 ? colorA : colorB;
          return (
            <motion.span
              key={`${ri}-${i}`}
              className="absolute"
              style={{
                width: 3,
                height: 13,
                borderRadius: 3,
                background: `linear-gradient(to top, ${color}00, ${color})`,
                boxShadow: `0 0 6px ${color}`,
              }}
              initial={{ x: 0, y: 0, opacity: 0, rotate: deg }}
              animate={{ x: [0, dx], y: [0, dy], opacity: [1, 1, 0], scale: [1, 0.5], rotate: deg }}
              transition={{ duration: 1.5 - ri * 0.15, delay, repeat: Infinity, repeatDelay: 2.4, ease: 'easeOut' }}
            />
          );
        })
      )}
    </>
  );
}

// All positions are fixed (deterministic) so SSR and client markup match.
const SPARKLES = [
  { left: '6%', top: '22%', size: 7, delay: 0, dur: 3.2 },
  { left: '14%', top: '68%', size: 5, delay: 0.7, dur: 4.0 },
  { left: '22%', top: '35%', size: 6, delay: 1.3, dur: 3.6 },
  { left: '34%', top: '78%', size: 4, delay: 0.3, dur: 4.4 },
  { left: '44%', top: '16%', size: 6, delay: 1.8, dur: 3.1 },
  { left: '56%', top: '60%', size: 5, delay: 0.9, dur: 3.9 },
  { left: '66%', top: '28%', size: 7, delay: 1.1, dur: 3.5 },
  { left: '76%', top: '72%', size: 5, delay: 0.5, dur: 4.2 },
  { left: '84%', top: '40%', size: 6, delay: 1.5, dur: 3.7 },
  { left: '92%', top: '20%', size: 4, delay: 0.2, dur: 4.1 },
  { left: '50%', top: '14%', size: 5, delay: 2.0, dur: 3.3 },
  { left: '30%', top: '12%', size: 4, delay: 1.0, dur: 3.8 },
];

const FIREWORKS = [
  { left: '18%', top: '22%', delay: 0 },
  { left: '40%', top: '14%', delay: 0.9 },
  { left: '62%', top: '20%', delay: 1.8 },
  { left: '84%', top: '16%', delay: 2.6 },
];

const ROCKETS = [
  { left: '18%', top: '24%', delay: 0, color: '#fbbf24' },
  { left: '40%', top: '16%', delay: 0.9, color: '#ef4444' },
  { left: '60%', top: '26%', delay: 1.7, color: '#22c55e' },
  { left: '82%', top: '18%', delay: 2.5, color: '#3b82f6' },
];
const ROCKET_RISE = 320;

const ANAARS = [
  { left: '12%', delay: 0.2 },
  { left: '50%', delay: 1.0 },
  { left: '88%', delay: 1.8 },
];
const ANAAR_PARTICLES = 14;

const CONFETTI = Array.from({ length: 20 }).map((_, i) => ({
  left: `${(i * 5.0 + 2) % 100}%`,
  delay: (i % 7) * 0.5,
  dur: 4 + (i % 5),
  rot: (i * 47) % 360,
}));
const CONFETTI_COLORS = ['#f59e0b', '#ef4444', '#22c55e', '#3b82f6', '#a855f7', '#fde047', '#06b6d4'];

const SNOW = Array.from({ length: 16 }).map((_, i) => ({
  left: `${(i * 6.1 + 3) % 100}%`,
  delay: (i % 6) * 0.6,
  dur: 6 + (i % 5),
  size: 6 + (i % 3) * 3,
}));

const FAIRY_BULBS = 30;
const DIYAS = 7;

// Sky lanterns that drift upward across the hero (deterministic → SSR-safe).
const SKY_LANTERNS = [
  { left: '12%', delay: 0, dur: 15, sway: 12, scale: 1 },
  { left: '26%', delay: 4, dur: 18, sway: -14, scale: 0.8 },
  { left: '42%', delay: 7.5, dur: 16, sway: 10, scale: 1.1 },
  { left: '58%', delay: 2, dur: 19, sway: -11, scale: 0.9 },
  { left: '72%', delay: 5.5, dur: 17, sway: 13, scale: 1 },
  { left: '88%', delay: 9, dur: 20, sway: -10, scale: 0.85 },
];

const SALE_TAGS = [
  { left: '8%', top: '30%', emoji: '🏷️', delay: 0 },
  { left: '88%', top: '26%', emoji: '💥', delay: 0.8 },
  { left: '16%', top: '72%', emoji: '%', delay: 1.4 },
  { left: '82%', top: '70%', emoji: '🔖', delay: 0.4 },
  { left: '46%', top: '14%', emoji: '%', delay: 1.1 },
];

/** Decorative background layer for the active campaign (behind hero content). */
export function CampaignHeroDecor() {
  const campaign = useActiveCampaign();
  const reduce = useReducedMotion();
  // Particle emitters animate from an invisible initial state; rendering them
  // during SSR/hydration flashes a frozen frame. Mount them only after the
  // client hydrates to avoid that flash.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const theme = campaign ? getCampaignTheme(campaign.slug) : null;
  if (!campaign || !theme) return null;
  const fx = theme.effects;
  const lightCols = theme.lightColors ?? [theme.gradientFrom, theme.gradientTo];
  const animate = mounted && !reduce;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" aria-hidden="true">
      {/* Shared gradients for the coded diyas + sky lanterns (rendered once). */}
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id="zs-clay" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#c2410c" />
            <stop offset="55%" stopColor="#7c2d12" />
            <stop offset="100%" stopColor="#431407" />
          </linearGradient>
          <radialGradient id="zs-flame" cx="50%" cy="70%" r="62%">
            <stop offset="0%" stopColor="#fff7cc" />
            <stop offset="45%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#f97316" />
          </radialGradient>
          <linearGradient id="zs-lantern" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fde68a" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#c2410c" />
          </linearGradient>
          <radialGradient id="zs-lantern-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(251,146,60,0.85)" />
            <stop offset="60%" stopColor="rgba(251,146,60,0.25)" />
            <stop offset="100%" stopColor="rgba(251,146,60,0)" />
          </radialGradient>
        </defs>
      </svg>

      {/* Full festive takeover: purple→gold radial wash + rangoli ornaments */}
      {theme.takeover && (
        <>
          <div className="absolute inset-0" style={{ background: theme.takeover.edge }} />
          <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 50% 40%, ${theme.takeover.center} 0%, ${theme.takeover.center}00 44%)`, opacity: 0.6 }} />
          <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 50% 118%, ${theme.takeover.mid} 0%, ${theme.takeover.mid}00 55%)`, opacity: 0.45 }} />
          <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 50% 40%, transparent 52%, ${theme.takeover.edge} 100%)` }} />
          <Mandala color="#fcd34d" className="absolute w-64 h-64 opacity-30" style={{ left: -72, bottom: -72 }} />
          <Mandala color="#fcd34d" className="absolute w-64 h-64 opacity-30" style={{ right: -72, bottom: -72 }} />
          <Mandala color="#fcd34d" className="absolute w-52 h-52 opacity-20" style={{ left: -60, top: -60 }} />
          <Mandala color="#fcd34d" className="absolute w-52 h-52 opacity-20" style={{ right: -60, top: -60 }} />
          <DiamondDivider color="#fcd34d" className="absolute left-1/2 -translate-x-1/2 w-44 opacity-70" style={{ top: 30 }} />
        </>
      )}

      {/* Warm festive glow */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[620px] h-[620px] rounded-full blur-3xl"
        style={{ backgroundColor: theme.glowColor }}
      />

      {/* Hanging fairy-light string (jhalar) draped across the top */}
      {fx.lights && (
        <div className="absolute top-0 left-0 right-0 h-12">
          <svg viewBox="0 0 1000 44" preserveAspectRatio="none" className="absolute inset-x-0 top-0 w-full h-12" fill="none" stroke="rgba(253,224,71,0.45)" strokeWidth="1.3" aria-hidden="true">
            <path d="M0 6 Q 62 30 125 6 T 250 6 T 375 6 T 500 6 T 625 6 T 750 6 T 875 6 T 1000 6" />
          </svg>
          {Array.from({ length: FAIRY_BULBS }).map((_, i) => {
            const t = i / (FAIRY_BULBS - 1);
            const dip = Math.abs(Math.sin(t * Math.PI * 8)) * 20;
            const color = lightCols[i % lightCols.length];
            return (
              <span key={i} className="absolute" style={{ left: `${t * 100}%`, top: 3 + dip }}>
                <span className="block w-px h-1.5 mx-auto" style={{ background: 'rgba(253,224,71,0.5)' }} />
                <motion.span
                  className="block rounded-full"
                  style={{ width: 8, height: 10, backgroundColor: color, boxShadow: `0 0 12px ${color}, 0 0 5px ${color}` }}
                  animate={reduce ? undefined : { opacity: [0.4, 1, 0.4] }}
                  transition={reduce ? undefined : { duration: 1.6, delay: (i % 5) * 0.25, repeat: Infinity, ease: 'easeInOut' }}
                />
              </span>
            );
          })}
        </div>
      )}

      {/* Rockets that climb (with tail) and burst (Diwali) */}
      {fx.rockets && animate && ROCKETS.map((r, ri) => (
        <div key={ri} className="absolute" style={{ left: r.left, top: r.top }}>
          <motion.span
            className="absolute"
            style={{ width: 3, height: 18, borderRadius: 3, background: `linear-gradient(to top, ${r.color}00, ${r.color})`, boxShadow: `0 0 10px ${r.color}` }}
            initial={{ y: ROCKET_RISE, opacity: 0 }}
            animate={{ y: [ROCKET_RISE, 0], opacity: [0, 1, 1, 0] }}
            transition={{ duration: 1.1, delay: r.delay, repeat: Infinity, repeatDelay: 2.6, ease: 'easeOut' }}
          />
          <Burst colorA={r.color} colorB="#fde047" delay={r.delay + 1.1} />
        </div>
      ))}

      {/* Flying sky lanterns drifting up (Diwali) */}
      {fx.flyingLamps && animate && SKY_LANTERNS.map((l, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: l.left, bottom: 0 }}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: [40, -640], x: [0, l.sway, -l.sway, 0], opacity: [0, 1, 1, 0.85, 0] }}
          transition={{ duration: l.dur, delay: l.delay, repeat: Infinity, ease: 'linear' }}
        >
          <motion.div
            style={{ filter: 'drop-shadow(0 0 9px rgba(251,146,60,0.75))' }}
            animate={{ scale: [1, 1.05, 1], rotate: [0, 1.5, -1.5, 0] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <SkyLantern scale={l.scale} />
          </motion.div>
        </motion.div>
      ))}

      {/* Anaar fountains along the bottom (Diwali) */}
      {fx.anaars && animate && ANAARS.map((a, ai) => (
        <div key={ai} className="absolute" style={{ left: a.left, bottom: '8%' }}>
          {Array.from({ length: ANAAR_PARTICLES }).map((_, i) => {
            const spread = ((i / (ANAAR_PARTICLES - 1)) - 0.5) * 70;
            const up = 70 + (i % 4) * 14;
            const color = i % 3 === 0 ? '#fde047' : i % 3 === 1 ? '#fb923c' : '#f59e0b';
            return (
              <motion.span
                key={i}
                className="absolute rounded-full"
                style={{ width: 4, height: 4, backgroundColor: color, boxShadow: `0 0 6px ${color}` }}
                initial={{ x: 0, y: 0, opacity: 0 }}
                animate={{ x: [0, spread], y: [0, -up, -up + 24], opacity: [1, 1, 0] }}
                transition={{ duration: 1.3, delay: a.delay + (i % 5) * 0.1, repeat: Infinity, repeatDelay: 0.3, ease: 'easeOut' }}
              />
            );
          })}
        </div>
      ))}

      {/* Fireworks (New Year) */}
      {fx.fireworks && animate && FIREWORKS.map((fw, fi) => (
        <div key={fi} className="absolute" style={{ left: fw.left, top: fw.top }}>
          <Burst colorA={theme.gradientFrom} colorB={theme.sparkleColor} delay={fw.delay} />
        </div>
      ))}

      {/* Confetti (New Year / Sale / New Drop) */}
      {fx.confetti && animate && CONFETTI.map((c, i) => (
        <motion.span
          key={i}
          className="absolute top-0"
          style={{ left: c.left, width: 7, height: 11, backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length], borderRadius: 1 }}
          initial={{ y: '-10%', rotate: c.rot, opacity: 0 }}
          animate={{ y: '115%', rotate: c.rot + 360, opacity: [0, 1, 1, 0.4], x: [0, 12, -10, 0] }}
          transition={{ duration: c.dur, delay: c.delay, repeat: Infinity, ease: 'linear' }}
        />
      ))}

      {/* Snow (Christmas) */}
      {fx.snow && animate && SNOW.map((f, i) => (
        <motion.span
          key={i}
          className="absolute top-0 text-white/80"
          style={{ left: f.left, fontSize: f.size }}
          initial={{ y: '-10%', opacity: 0 }}
          animate={{ y: '110%', opacity: [0, 1, 1, 0.3], x: [0, 10, -8, 0] }}
          transition={{ duration: f.dur, delay: f.delay, repeat: Infinity, ease: 'linear' }}
        >
          ❄
        </motion.span>
      ))}

      {/* Santa sleigh flying across (Christmas) */}
      {fx.santa && animate && (
        <motion.div
          className="absolute text-3xl sm:text-4xl"
          style={{ top: '12%' }}
          initial={{ x: '-15vw' }}
          animate={{ x: ['-15vw', '115vw'], y: [0, -10, 6, 0] }}
          transition={{ duration: 10, repeat: Infinity, repeatDelay: 4, ease: 'easeInOut' }}
        >
          🛷🎅
        </motion.div>
      )}

      {/* Floating sale tags (Mega Sale) */}
      {fx.saleTags && animate && SALE_TAGS.map((t, i) => (
        <motion.span
          key={i}
          className="absolute flex items-center justify-center font-extrabold text-amber-300 text-xl sm:text-2xl"
          style={{ left: t.left, top: t.top, textShadow: '0 0 10px rgba(245,158,11,0.8)' }}
          animate={{ y: [0, -14, 0], rotate: [-8, 8, -8], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 3 + (i % 3), delay: t.delay, repeat: Infinity, ease: 'easeInOut' }}
        >
          {t.emoji === '%' ? <span className="px-2 py-0.5 rounded-md bg-amber-500/20 border border-amber-400/40">%</span> : t.emoji}
        </motion.span>
      ))}

      {/* Sparkles */}
      {fx.sparkles && SPARKLES.map((s, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            backgroundColor: theme.sparkleColor,
            boxShadow: `0 0 ${s.size * 2.5}px ${theme.sparkleColor}`,
          }}
          animate={reduce ? undefined : { opacity: [0.25, 1, 0.25], y: [0, -16, 0], scale: [0.9, 1.25, 0.9] }}
          transition={reduce ? undefined : { duration: s.dur, delay: s.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      {/* Coded clay diyas along the bottom (Diwali) */}
      {fx.diyas && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center items-end gap-6 sm:gap-12">
          {Array.from({ length: DIYAS }).map((_, i) => (
            <Diya key={i} delay={i * 0.22} reduce={reduce} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Loud festive offer chip with a gradient CTA button, near the headline. */
export function CampaignOfferChip() {
  const campaign = useActiveCampaign();
  const reduce = useReducedMotion();
  const theme = campaign ? getCampaignTheme(campaign.slug) : null;
  if (!campaign || !theme) return null;

  const grad = `linear-gradient(135deg, ${theme.gradientFrom}, ${theme.gradientTo})`;
  const isExternal = campaign.cta_link.startsWith('http');

  const inner = (
    <motion.span
      className="relative inline-flex items-center gap-2.5 py-2 pl-3 pr-2 rounded-full font-bold"
      style={{
        border: '1.5px solid transparent',
        backgroundImage: `linear-gradient(rgba(8,12,18,0.9), rgba(8,12,18,0.9)), ${grad}`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      }}
      animate={reduce ? undefined : {
        boxShadow: [
          `0 0 16px ${theme.gradientFrom}55`,
          `0 0 30px ${theme.gradientTo}99`,
          `0 0 16px ${theme.gradientFrom}55`,
        ],
      }}
      transition={reduce ? undefined : { duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    >
      <motion.span
        className="text-xl leading-none"
        animate={reduce ? undefined : { rotate: [0, 12, -12, 0], scale: [1, 1.15, 1] }}
        transition={reduce ? undefined : { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
      >
        {theme.emoji}
      </motion.span>
      <span className="text-sm sm:text-base text-white">{campaign.offer_text || campaign.name}</span>
      <motion.span
        className="inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-white text-sm font-extrabold whitespace-nowrap shadow-lg"
        style={{ backgroundImage: grad }}
        animate={reduce ? undefined : { scale: [1, 1.06, 1] }}
        transition={reduce ? undefined : { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      >
        {campaign.cta_label} &rarr;
      </motion.span>
    </motion.span>
  );

  return (
    <motion.div
      className="inline-block mb-4"
      initial={{ opacity: 0, y: -8, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      {isExternal ? (
        <a href={campaign.cta_link} target="_blank" rel="noopener noreferrer">{inner}</a>
      ) : (
        <Link to={campaign.cta_link}>{inner}</Link>
      )}
    </motion.div>
  );
}
